import type { Dictionary } from "./dictionary.ts";

export const en = {
	app: {
		name: "CMS Analysis",
		language: "Language",
		localeName: { de: "German", en: "English" },
	},
	nav: {
		dashboard: "Dashboard",
		catalog: "Catalog",
		products: "Products",
		personas: "Personas",
		questionnaires: "Questionnaires",
		authors: "Authors",
		views: "Views",
		overview: "Answer overview",
		compare: "Compare",
		reports: "Reports",
		customers: "Customers",
		start: "Start",
		persona: "Persona",
		logout: "Sign out",
		collapse: "Hide navigation",
		expand: "Show navigation",
	},
	auth: {
		title: "Sign in",
		token: "Access token",
		signIn: "Sign in",
		signingIn: "Signing in",
		refused: "The token was refused.",
	},
	preview: {
		viewing: "You are seeing the view of",
		exit: "Leave preview",
		readOnly: "Nothing can be changed in the preview.",
		enter: "View as customer",
	},
	customers: {
		title: "Customers",
		newCustomer: "New customer",
		users: "Users",
		access: "Access",
		history: "History",
		team: "Team",
		teamIntro:
			"Consultants without a customer. Each gets a personal token, so the history names people instead of a shared key.",
		products: "Products in scope",
		productsHint:
			"Empty means none. Products without answers stay hidden from the customer, so nobody meets empty boxes.",
		withoutData: "no data",
		assign: "Assign",
		unassign: "Unassign",
		nothingAssigned: "Nothing assigned yet.",
		noneFree: "Nothing unassigned is left to add.",
		openQuestionnaires: "Open questionnaires",
		newestReport: "Newest report",
		userCount: "Users",
		inactive: "Inactive",
		relevancyQuestionnaires: "Relevancy questionnaires",
		notFound: "There is no such customer.",
	},
	users: {
		newUser: "New user",
		role: "Role",
		roleAdmin: "Consultant (admin)",
		roleCustomer: "Customer",
		author: "Author",
		authorHint:
			"Optional. Links the user to the author questionnaires are addressed to.",
		tokenPrefix: "Token starts with",
		lastSeen: "Last seen",
		never: "Never",
		issuedTitle: "Access token",
		issuedIntro: "Hand this token over securely. It will not be shown again.",
		understood: "I have saved the token",
		rotate: "Rotate token",
		rotateHint: "The current token and every session stop working.",
		revoke: "Revoke token",
		revoked: "Revoked",
		none: "No users yet.",
	},
	portal: {
		startTitle: "Start",
		open: "Open questionnaires",
		noOpen: "Nothing open. Every questionnaire has been submitted.",
		fill: "Fill in",
		reports: "Reports",
		noReports: "No reports yet.",
		compare: "Go to the comparison",
		compareHint: "Your personas are already preselected.",
		empty: "Your consultant has not released any data yet.",
		draft: "Draft",
		lockedOn: "Locked on",
		submittedOn: "Submitted on",
		personaTitle: "Persona",
		ratings: "Weighting",
		noRatings: "No weighting decided yet.",
		ratingsOpen:
			"The relevancy questionnaire is still open. The weighting appears here once it is submitted and accepted.",
		requirements: "Must-haves",
		noRequirements: "No must-haves decided.",
		fillRelevancy: "Fill in the relevancy questionnaire",
		compareFor: "Comparison for this persona",
		noPersona: "No persona is assigned to you yet.",
		noWeighting: "Without a persona the comparison shows the unweighted scores.",
		noProducts: "No products have been released to you yet.",
	},
	common: {
		of: "of",
		optional: "optional",
		retry: "Try again",
		loading: "Loading",
		saving: "Saving",
		saved: "Saved",
		saveFailed: "Saving failed",
		notAssessed: "Not assessed",
		coverage: "Coverage",
		score: "Score",
		band: "Band",
		answer: "Answer",
		yes: "Yes",
		no: "No",
		dontKnow: "I don't know",
		none: "None",
		all: "All",
		create: "Create",
		save: "Save",
		cancel: "Cancel",
		copy: "Copy",
		copied: "Copied",
		edit: "Edit",
		add: "Add",
		new: "New",
		empty: "Nothing here yet",
		identity: "Id",
		name: "Name",
		title: "Title",
		label: "Label",
		description: "Description",
		active: "Active",
		status: "Status",
		product: "Product",
		topic: "Topic",
		capability: "Property",
		question: "Question",
		persona: "Persona",
		author: "Author",
		weight: "Weight",
		actions: "Actions",
		resizeColumn: "Column width",
		resizeColumnHint: "Drag or use the arrow keys. Double-click restores the automatic widths.",
		notAvailable: "Not available",
		customer: "Customer",
		customerHint:
			"Without a customer the entry stays internal and is visible to consultants only.",
		internal: "Internal",
		noCustomer: "No customer (internal)",
	},
	band: {
		missing: "Missing or weak",
		partial: "Partial",
		solid: "Solid",
		excellent: "Excellent",
	},
	error: {
		network: "The connection failed",
		decode: "The response could not be read",
		notFound: "Not found",
		validation: "The input does not fit the model",
		conflict: "The current state does not allow this",
		inUse: "Still in use",
		unauthorized: "The session is missing or has expired.",
		forbidden: "You are not allowed to do this.",
		internal: "The server did not answer the request.",
	},
	remove: {
		action: "Delete",
		title: "Delete this?",
		confirm: "Delete",
		deleting: "Deleting …",
		blockedTitle: "Still in use",
		blockedIntro: "These entries still point at it:",
		cascadeWarning: "will be deleted too",
		detachNote: "reference will be removed",
		forceLabel: "Delete anyway",
		forceHint:
			"Entries that make no sense without this one are deleted with it. Everywhere else, only the reference is removed.",
		forceConfirm: "Delete with everything that depends on it",
		done: "Deleted",
	},
	answer: {
		sourceHuman: "Human",
		sourceAi: "AI",
		confidenceHigh: "high confidence",
		confidenceLow: "low confidence",
		history: "Answer history",
		validFrom: "Valid from",
		validTo: "Valid to",
		value: "Value",
		noAnswers: "No answers recorded for this product yet.",
	},
	dashboard: {
		title: "Dashboard",
		questionnaires: "Questionnaires",
		changes: "Latest changes",
		entity: "Entity",
		actor: "Caused by",
		at: "When",
		scores: "Products with their score",
		scoresFromLocked: "From the most recent locked report.",
		scoresFromLive: "Computed live, not written down yet.",
		noEvaluation: "Nothing scored yet. Create a report to see scores here.",
	},
	compare: {
		title: "Compare products",
		productA: "Product A",
		productB: "Product B",
		swap: "Swap",
		level: "Level",
		levelTopic: "Topics",
		levelCapability: "Properties",
		axes: "Axes",
		allAxes: "All",
		noAxes: "None",
		noAxesAvailable: "Nothing is scored for this pair.",
		noAxesPicked: "Pick at least one axis.",
		reset: "Biggest differences",
		samePair: "Pick two different products.",
		tableCaption: "Both products' scores on every axis.",
		view: "View",
		viewHint: "A view narrows the products and topics and is evaluated live.",
		newestReport: "Newest report",
		sourceView: "Scores from a live evaluation of the chosen view.",
		tooFew: "Fewer than two products are scored here, so there is nothing to compare.",
		differences: "Where they differ",
		differencesIntro:
			"The picked axes by distance, widest first. The bar shows how far apart the two stand, and which way.",
		gap: "Distance",
		leads: "Leads",
		noDifferences: "The pair is level on every axis you picked.",
		tiedCount: "Level",
		oneSidedCount: "Scored for one product only",
		partsCapabilities: "Properties in this topic",
		partsQuestions: "Questions behind this property",
		noParts: "No question is attached to this axis.",
		partTied: "Same score",
		partOneSided: "One side only",
		persona: "Weighting",
		personaNone: "No persona weighting",
		personaHint: "Shows what this persona's weighting makes of the comparison.",
		weightedFor: "weighted for",
		weightedUp: "rises under the weighting",
		weightedDown: "falls under the weighting",
		weightedLevel: "unchanged under the weighting",
		weightHeader: "Importance",
		weightOf: "Importance for",
		weightedGap: "Distance after weighting",
		weightedGapKey:
			"The thin line beneath is the same distance after the weighting: an axis rated ++ counts double, one rated -- not at all.",
		notRated: "has not rated anything yet. Until then every axis counts the same.",
		explanation: "Reasoning",
		showExplanation: "Read the reasoning",
		noExplanation: "No reasoning was recorded for this answer.",
		noAnswer: "This product has no answer here.",
		comment: "Remark",
		answeredAs: "Given as",
		inCapability: "Question on the property",
	},
	history: {
		title: "Change log",
		link: "History",
		at: "When",
		actor: "Caused by",
		change: "Change",
		asOfJump: "Report as of this change",
		noEvents: "No change is recorded for this entity yet.",
		created: "Created",
		updated: "Updated",
		deleted: "Deleted",
		translation: "Translation",
		draftAnswer: "Draft answer",
		unknown: "Unknown change",
	},
	tree: {
		selectAll: "Whole catalog",
		selected: "selected",
		expand: "Unfold",
		collapse: "Fold",
		expandAll: "Unfold everything",
		collapseAll: "Fold everything",
		clear: "Select nothing",
		unassigned: "Not in any topic",
		empty: "The catalog holds no questions yet.",
	},
	coverage: {
		label: {
			counted: "Counted",
			outside: "Not in view",
			unanswered: "Unanswered",
		},
		hint: {
			counted:
				"At least one answer is inside this view, so it reaches the report.",
			outside:
				"Answered, but this view reaches none of those answers. Usually no property in scope derives from this question.",
			unanswered:
				"No answer has ever been recorded for this question, for any product.",
		},
		view: "Measure against view",
		viewHint:
			"Coverage is judged against this view. Without one, against a view that narrows nothing.",
		noView: "No view",
	},
	views: {
		title: "Views",
		newView: "New view",
		topics: "Topics",
		capabilities: "Properties",
		personas: "Personas",
		products: "Products",
		questionnaires: "Questionnaires",
		allHint: "Switch this off to narrow the view to a chosen few.",
		hollow:
			"Nothing is picked, which would mean the same as leaving this on all. Pick at least one.",
		narrows: "Narrowed to",
		everything: "Everything",
		none: "No view exists yet, and a report needs one.",
	},
	overview: {
		title: "Answer overview",
		caveat:
			"These averages are not report scores. A topic average is the plain mean of the questions a product answered, with no property weight and no relevancy applied.",
		topicAverage: "Topic average",
		average: "Average",
		answeredOf: "answered of",
		origin: "Origin",
		noAnswerYet: "No answer",
		unassigned: "Questions without a topic",
		unassignedHint:
			"No topic lists these, so they appear in no tree and in no report. Only the number of answers on file is shown: without a topic no precedence has been applied, so none of them can be called the answer that counts. Give the question a topic to fix this.",
		recorded: "on file",
		onlyAnswered: "Only questions with answers",
		onlyAnsweredHint:
			"Hides everything nobody has answered yet, in any product.",
		noProducts: "No product exists yet, so there is nothing to compare.",
		empty: "No question carries an answer yet.",
	},
	catalog: {
		title: "Catalog",
		relevancy: "Relevancy",
		capabilities: "Properties",
		questions: "Questions",
		questionsLinked: "linked questions",
		informational: "Context only",
		newTopic: "New topic",
		newCapability: "New property",
		newQuestion: "New question",
		answerType: "Answer type",
		linkedQuestions: "Linked questions",
		linkedQuestionsHint:
			"Comma separated ids; properties may share questions.",
		topicQuestions: "Questions in this topic",
		topicQuestionsHint:
			"Tick the questions this topic lists. A question shows up in the tree only through a topic that lists it.",
		capabilityDescriptionHint:
			"Shown to recipients in the relevancy questionnaire. Say what the property covers, so a rating does not rest on the label alone.",
		owningTopicHint: "The topic this property is scored under.",
		questionTopicHint:
			"The topic that lists this question. Ticking another one moves it.",
		questionTopicRequired:
			"Without a topic the question would not appear in the tree at all.",
		unassignedCapabilities: "Properties without a topic",
		unassignedCapabilitiesHint:
			"A property appears in the tree only through the topic it names. These name none, or one that no longer exists, so they sit outside the catalog and no report can reach the questions they derive from. Give each one a topic.",
		danglingTopic: "Names topic",
		capabilityTopicRequired:
			"Without a topic the property appears nowhere in the catalog, and no report reaches the questions it derives from.",
		compareAnswers: "Compare answers",
		compareAnswersHint:
			"Every product on this one question, row by row: the answer matrix narrowed to a single question.",
		search: "Search",
		searchHint:
			"Narrows the tree to topics, properties and questions whose title comes close. Close matches count too.",
		searchPlaceholder: "Type part of a title",
		noMatches: "Nothing in the catalog comes close to that.",
	},
	products: {
		title: "Products",
		newProduct: "New product",
		bandOverview: "Bands per topic",
		chooseView: "View",
		viewHint: "The bands come from an evaluation of this view.",
		references: "Reference projects",
		overview: "Overview",
		answers: "Answers",
		dossier: "Dossier",
		details: "Details",
		tags: "Tags",
		tagsHint:
			"Short labels a reader orients by, such as EU hosting, Germany, or open source. Enter or a comma adds one.",
		tagsLabel: "Tags of this product",
		addTag: "Add",
		removeTag: "Remove tag",
		noTags: "No tags yet.",
		noReferences: "No reference projects yet.",
		noDescription: "No description yet.",
		noDossier: "No dossier has been written for this product yet.",
		editDossier: "Edit dossier",
		dossierHint:
			"Markdown: headings, lists, tables, bold, italics and links. A front matter block between two lines of three dashes at the top becomes a fact sheet.",
		write: "Write",
		preview: "Preview",
		unsaved: "Unsaved changes",
		editDetails: "Edit details",
	},
	personas: {
		title: "Personas and requirements",
		newPersona: "New persona",
		requirements: "Requirements",
		newRequirement: "New requirement",
		minimumKind: "Threshold",
		minimumScore: "Minimum score",
		minimumBand: "Minimum band",
		rationale: "Rationale",
		rationaleHint:
			"This is what makes the knockout defensible, so it is mandatory.",
		rationaleRequired: "Without a rationale the requirement cannot be created.",
		view: "View",
	},
	authors: {
		title: "Authors",
		newAuthor: "New author",
		email: "Email",
		comment: "Remark",
	},
	questionnaires: {
		title: "Questionnaires",
		workbook: "Workbook",
		workbookHint:
			"The workbook is written for one product. Filled in, it can be read back.",
		workbookProduct: "Product",
		newQuestionnaire: "New questionnaire",
		productHint:
			"The one product these answers describe. The recipient never sees the others.",
		noProduct:
			"This questionnaire names no product, so it cannot be answered or exported.",
		scope: "Scope",
		scopeHint:
			"Tick the whole catalog, a subtree, or single questions. Ticking a topic keeps questions later added to it in scope.",
		noAuthors: "No author exists yet, and a questionnaire needs one.",
		addAuthor: "Add an author",
		answers: "Answers attributed to this questionnaire",
		answersHint:
			"Everything recorded against it, including answers written straight through the tool surface rather than the link.",
		noAnswers: "No answer carries this questionnaire yet.",
		magicLink: "Magic link",
		copyLink: "Copy link",
		review: "Review answers",
		accept: "Accept answers",
		accepted: "Accepted",
		drafts: "Submitted answers",
		noDrafts: "Nothing has been entered yet.",
		statusPending: "Pending",
		statusReceived: "Opened",
		statusAnswered: "Submitted",
		statusEvaluated: "Accepted",
		created: "Created",
	},
	relevancy: {
		title: "Relevancy",
		ratings: "Ratings",
		noRatings: "No rating yet.",
		rating: "Step",
		note: "Note",
		weight: "Weight",
		weighting: "Weighting",
		newQuestionnaire: "New relevancy questionnaire",
		scope: "Scope",
		scopeHint:
			"Tick topics to ask about their properties. With nothing ticked, the persona's view applies.",
		magicLink: "Magic link",
		copyLink: "Copy link",
		accept: "Accept ratings",
		accepted: "Accepted",
		review: "Review",
		questionnaires: "Relevancy questionnaires",
		noQuestionnaires: "No relevancy questionnaire yet.",
		noAuthors: "Without a recipient there is nothing to pick.",
		scale: {
			much_less: "Irrelevant",
			less: "Less important",
			equal: "Average",
			more: "More important",
			much_more: "Most important",
		},
	},
	relevancyFill: {
		title: "What matters to you?",
		noteTitle: "Why this is worth a quarter of an hour",
		noteWhat:
			"On this page you set priorities rather than scores. You tell us which of the topics below genuinely matter to your organisation. How well each system covers them is something we check for you afterwards.",
		noteWhy:
			"Every headless CMS is strong in some areas and weak in others. Without your weighting each criterion counts the same, so the comparison ends up recommending a system that excels where you will never need it.",
		noteHow:
			"So take your time with the topics and read the short explanation under each property. Rate honestly: if everything is very important, nothing is. Your picks feed straight into the recommendation.",
		greeting: "Hello",
		progressTitle: "Progress",
		setAll: "Set the whole topic",
		addNote: "Add a note",
		note: "Note",
		noteHint: "Optional context for the analyst; it never changes the score.",
		submit: "Submit",
		submitting: "Submitting",
		submitHint:
			"Once you submit, we see your weighting; nothing is fixed before that.",
		confirmSubmit: "Submit now? The link closes afterwards.",
		confirmYes: "Yes, submit",
		loadFailed: "The weighting could not be loaded.",
		noCapabilities: "This questionnaire asks about no property.",
		skipToCapabilities: "Skip to the properties",
	},
	reports: {
		title: "Reports",
		downloadWorkbook: "Download workbook",
		builder: "Build a report",
		view: "View",
		primaryQuestionnaire: "Primary questionnaire",
		mergeOrder: "Source order",
		mergeOrderHint: "Gaps are filled in this order.",
		knockoutMode: "Requirement handling",
		knockoutEnforce: "Enforce",
		knockoutAnnotate: "Annotate",
		knockoutIgnore: "Ignore",
		asOf: "As of",
		asOfHint: "Empty means the current state.",
		evaluateNow: "Evaluate",
		persist: "Save report",
		lock: "Lock",
		locked: "Locked",
		live: "Still follows changes",
		ranking: "Ranking",
		perTopic: "Bands per topic",
		personaRankings: "Rankings per persona",
		violations: "Requirement violations",
		openRequirements: "Open requirements",
		contributing: "Contributing answers",
		minimum: "Demanded",
		actual: "Reached",
		excluded: "Does not meet requirements",
		requirementsSkipped: "Requirements were not evaluated.",
		formulaVersion: "Formula version",
		weightedScore: "Weighted",
		globalScore: "Global",
		reorderedByRatings: "Order differs",
		reorderedHint:
			"This persona's weighting orders the products differently from the global evaluation.",
		appliedWeights: "Applied weights",
		capabilities: "Properties in detail",
		drillDown: "Details",
		noReport: "Nothing evaluated yet.",
		noProducts:
			"Nothing to rank: no product falls inside this report. The owner sees only products they list and that carry answers; check the customer's product list and the view.",
		noAnswers:
			"Nothing to score: no answer falls inside this report. Check the view's topics, capabilities and questionnaires.",
		status: "Status",
		preview: "Preview",
		previewHint:
			"This is how the customer sees the report once it is saved. Until then the evaluation lives only on this screen.",
		saved: "Saved reports",
	},
	fill: {
		title: "Questionnaire",
		greeting: "Hello",
		progressTitle: "Progress",
		progressAnswered: "answered",
		informational: "For context only, never scored",
		product: "Product",
		productMissing: "Please pick the product you are describing first.",
		comment: "Comment",
		commentHint: "A short note on your answer.",
		explanation: "Explanation",
		explanationHint: "Background, constraints or pointers to documentation.",
		dontKnowHint:
			"Leaving this empty counts as “I don't know” and stays unscored.",
		ratingHint: "0 means not supported, 5 means available out of the box.",
		textHint: "Free text, never scored.",
		multiSelectHint: "Pick as many as apply.",
		submit: "Submit questionnaire",
		submitting: "Submitting",
		submitHint: "After submitting, the form can no longer be edited.",
		loadFailed: "The questionnaire could not be loaded.",
		noQuestions: "This questionnaire currently has no question in scope.",
		skipToQuestions: "Skip to the questions",
	},
	dead: {
		unavailableTitle: "Link not valid",
		unavailableBody:
			"This link is unknown or no longer open. Please check the address or ask us for a new one.",
		submittedTitle: "Thank you",
		submittedBody: "Your answers reached us. You can close this window.",
	},
} satisfies Dictionary;
